// The office's own pages: Home, To do, Inbox and the receptionist, Insights, Books, Head office, Security, Search, Marketing.
import { createRoute } from "@tanstack/react-router";
import { AgencyHome } from "../../screens/Agency";
import { Books } from "../../screens/Books";
import { HeadOffice } from "../../screens/HeadOffice";
import { Home } from "../../screens/Home";
import { Inbox } from "../../screens/Inbox";
import { Insights } from "../../screens/Insights";
import { Marketing } from "../../screens/Marketing";
import { SearchPhone } from "../../screens/SearchPhone";
import { Security } from "../../screens/Security";
import { ReceptionistPage } from "../../screens/settings/Eunice";
import { FeatureSettings } from "../../screens/settings/Feature";
import { ReviewsSettings } from "../../screens/settings/More";
import { TodoFix } from "../../screens/TodoFix";
import { Allowed, app, ByTrade, Moved, page, Start } from "./base";

/** The Inbox; ?thread= opens that conversation (the receptionist page links each reply here). */
const inboxPage = createRoute({
  getParentRoute: () => app,
  path: "/inbox",
  validateSearch: (s: Record<string, unknown>): { thread?: string } =>
    typeof s.thread === "string" && /^[0-9a-f-]{36}$/.test(s.thread) ? { thread: s.thread } : {},
  component: function InboxRoute() {
    const { thread } = inboxPage.useSearch();
    return (
      <Allowed action="client.read">
        <Inbox key={thread ?? ""} {...(thread ? { open: thread } : {})} />
      </Allowed>
    );
  },
});

/** A Home To do item as a list to fix from (screens/TodoFix.tsx). Each list checks its own access on the server. */
const todoFix = createRoute({
  getParentRoute: () => app,
  path: "/todo/$kind",
  component: () => (
    <Allowed action="company.read">
      <TodoFix />
    </Allowed>
  ),
});

export const officeRoutes = [
  todoFix,
  page("/", Start),
  page("/home", () => (
    <Allowed action="company.read">
      <ByTrade agency={<AgencyHome />} other={<Home />} />
    </Allowed>
  )),
  inboxPage,
  page("/insights", () => (
    <Allowed action="insights.read">
      <Insights />
    </Allowed>
  )),
  page("/books", () => (
    <Allowed action="books.read">
      <Books />
    </Allowed>
  )),
  page("/head-office", () => (
    <Allowed action="hq.read">
      <HeadOffice />
    </Allowed>
  )),
  page("/security", () => (
    <Allowed action="security.read">
      <Security />
    </Allowed>
  )),
  page("/search", () => (
    <Allowed action="client.read">
      <SearchPhone />
    </Allowed>
  )),
  page("/marketing/settings", () => (
    <Allowed action="settings.read">
      <FeatureSettings title="Marketing settings" back="/marketing" backLabel="Marketing">
        <ReviewsSettings />
      </FeatureSettings>
    </Allowed>
  )),
  // The receptionist has his own page in the side menu (owner, 2026-11-01); Inbox settings was only him.
  page("/inbox/settings", () => <Moved to="/receptionist" />),
  page("/receptionist", () => (
    <Allowed action="settings.read">
      <ReceptionistPage />
    </Allowed>
  )),
  page("/marketing", () => (
    <Allowed action="client.read">
      <Marketing />
    </Allowed>
  )),
] as const;
